import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wrench, Loader2, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("thortek_token", data.token);
      toast.success(`Welcome back, ${data.user.name}.`);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1215] text-[#F4F2EE] hero-grid flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="rounded-2xl border border-white/10 bg-[#1E242B]/90 backdrop-blur-sm p-10">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-10 rounded-sm bg-[#E06A26] flex items-center justify-center">
              <Wrench className="w-5 h-5 text-[#0F1215]" strokeWidth={2.5} />
            </span>
            <span className="font-display font-extrabold tracking-[0.18em] text-lg">
              THOR<span className="text-[#E06A26]">TEK</span>
            </span>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#606D7A] mb-8">Dispatch console · restricted</p>

          <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#94A0AC] mb-2" htmlFor="al-email">Email</label>
              <input
                id="al-email"
                data-testid="admin-login-email-input"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0F1215]/80 border border-white/10 rounded-xl px-4 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-[#E06A26]/60"
                placeholder="admin@thortekappliance.com"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#94A0AC] mb-2" htmlFor="al-password">Password</label>
              <input
                id="al-password"
                data-testid="admin-login-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0F1215]/80 border border-white/10 rounded-xl px-4 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-[#E06A26]/60"
                placeholder="••••••••••••"
              />
            </div>

            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/25 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            <button
              data-testid="admin-login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#E06A26] hover:bg-[#F07A34] disabled:opacity-60 text-[#0F1215] font-semibold py-4 rounded-full flex items-center justify-center gap-3 transition-all duration-300"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Verifying…" : "Enter console"}
            </button>
          </form>
        </div>

        <Link to="/" data-testid="admin-login-back-link" className="mt-6 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#606D7A] hover:text-[#94A0AC] transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to site
        </Link>
      </motion.div>
    </div>
  );
}
